/**
 * What counts as tab-reachable, in one place.
 *
 * **Lifted out of `useFocusTrap` rather than copied into `useRegionRotor`.**
 * This directory's other module already records what happens when one idea gets
 * three implementations — `focusFirstAlive` exists because "move focus and
 * confirm it landed" had been written three times with three behaviours. A
 * second focusable-element selector would have been the same mistake one
 * directory over, and `web-architect` called it on the review that added the
 * rotor.
 *
 * It lives in `a11y/` and not in `overlay/` because the dependency has to point
 * this way: the trap and the rotor are both consumers, and the lower layer
 * cannot import from the higher one.
 */

/**
 * Deliberately excludes `[tabindex="-1"]`: programmatically focusable is not
 * the same as tab-reachable, and including it would make a trap's wrap stop on
 * things Tab never visits. `:disabled` is excluded for the same reason — a
 * disabled confirm button is skipped by the browser and must be skipped here,
 * or the wrap lands on something that cannot take focus and falls through.
 */
export const FOCUSABLE = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Excludes `[hidden]`, which is in the DOM and not tabbable.
 *
 * **It does NOT exclude `display: none` set by a stylesheet**, and the comment
 * carried over from `useFocusTrap` claimed otherwise — it described an
 * `offsetParent` check that the code has never performed. Corrected here
 * because this file is now the single source, and a wrong comment in a shared
 * module is the first thing every consumer trusts.
 *
 * **The gap costs the two consumers differently, and an earlier version of this
 * paragraph got that wrong in the same way the paragraph it replaced did.** It
 * claimed both confirm the move. Only one does:
 *
 *   - `useRegionRotor` confirms, via `focusFirstAlive`, so a `display: none`
 *     first child costs it a wasted candidate and it moves to the next.
 *   - `useFocusTrap` does NOT. `items[0]?.focus()` and the wrap's
 *     `first.focus()` / `last.focus()` are unchecked — its two
 *     `document.activeElement` reads decide which END the wrap is at, not
 *     whether anything landed. So a dialog whose first tab-reachable child is
 *     `display: none` opens with focus never moved, outside its own trap.
 *
 * That is the trap's pre-existing behaviour, not this module's to change, and
 * it is filed. Recorded here because this file is the shared source and its
 * comment is what a reader trusts about both callers.
 *
 * Neither can be tested for it here: jsdom lays nothing out, so
 * `display: none` is focusable in the unit suite and the asymmetry is
 * invisible.
 */
export function focusableIn(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => !el.hasAttribute('hidden'),
  )
}
